import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Assessment as ReportIcon,
} from '@mui/icons-material';
import axios from 'axios';

const OrderConsolidation = () => {
  const [consolidationData, setConsolidationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [schedule, setSchedule] = useState({
    frequency: 'weekly',
    day: 'Wednesday',
    time: '10:00',
    auto_consolidate: true
  });
  const [stats, setStats] = useState({
    pending_orders: 0,
    total_products: 0,
    estimated_savings: 0,
    last_consolidation: null
  });
  const [alertMessage, setAlertMessage] = useState({ show: false, type: '', message: '' });

  useEffect(() => {
    fetchConsolidationData();
    fetchSchedule();
    fetchStats();
  }, []);

  const API_BASE = 'https://b1.envicblue.co.uk/api';
  const token = localStorage.getItem('ward_stock_token');

  const axiosConfig = {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };

  const fetchConsolidationData = async () => {
    setLoading(true);
    try {
      // FIXED: Correct endpoint URL
      const response = await axios.get(`${API_BASE}/consolidation.php?action=pending`, axiosConfig);
      console.log('Consolidation response:', response.data);
      setConsolidationData(response.data.data || []);
    } catch (error) {
      console.error('Consolidation fetch error:', error);
      showAlert('error', error.response?.data?.message || 'Failed to fetch consolidation data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedule = async () => {
    try {
      // FIXED: Correct endpoint URL
      const response = await axios.get(`${API_BASE}/consolidation.php?action=schedule`, axiosConfig);
      if (response.data.data) {
        setSchedule(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch schedule:', error);
    }
  };

  const fetchStats = async () => {
    try {
      // FIXED: Correct endpoint URL
      const response = await axios.get(`${API_BASE}/consolidation.php?action=stats`, axiosConfig);
      console.log('Stats response:', response.data);
      setStats(response.data.data || stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const runConsolidation = async () => {
    if (!window.confirm('Are you sure you want to run consolidation for all pending orders?')) {
      return;
    }

    setLoading(true);
    try {
      // FIXED: Correct endpoint URL
      const response = await axios.post(`${API_BASE}/consolidation.php?action=run`, {}, axiosConfig);
      showAlert('success', response.data.message || 'Consolidation completed successfully!');
      fetchConsolidationData();
      fetchStats();
    } catch (error) {
      console.error('Consolidation run error:', error);
      showAlert('error', error.response?.data?.message || 'Failed to run consolidation');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setOpenDialog(true);
  };

  const handleApprove = async (orderId) => {
    try {
      // FIXED: Correct endpoint URL
      await axios.post(`${API_BASE}/consolidation.php?action=approve/${orderId}`, {}, axiosConfig);
      showAlert('success', 'Order approved successfully!');
      fetchConsolidationData();
      setOpenDialog(false);
    } catch (error) {
      console.error('Approve error:', error);
      showAlert('error', error.response?.data?.message || 'Failed to approve order');
    }
  };

  const handleReject = async (orderId, reason) => {
    try {
      // FIXED: Correct endpoint URL
      await axios.post(`${API_BASE}/consolidation.php?action=reject/${orderId}`, { reason }, axiosConfig);
      showAlert('success', 'Order rejected successfully!');
      fetchConsolidationData();
      setOpenDialog(false);
    } catch (error) {
      console.error('Reject error:', error);
      showAlert('error', error.response?.data?.message || 'Failed to reject order');
    }
  };

  const handleUpdateQuantity = async (orderId, newQuantity) => {
    try {
      await axios.put(`${API_BASE}/consolidation.php?action=update/${orderId}`, 
        { quantity: newQuantity }, 
        axiosConfig
      );
      showAlert('success', 'Quantity updated successfully!');
      fetchConsolidationData();
    } catch (error) {
      console.error('Update error:', error);
      showAlert('error', error.response?.data?.message || 'Failed to update quantity');
    }
  };

  const saveSchedule = async () => {
    try {
      // FIXED: Correct endpoint URL
      await axios.post(`${API_BASE}/consolidation.php?action=schedule`, schedule, axiosConfig);
      showAlert('success', 'Schedule updated successfully!');
      setScheduleDialog(false);
      fetchSchedule();
    } catch (error) {
      console.error('Schedule save error:', error);
      showAlert('error', error.response?.data?.message || 'Failed to update schedule');
    }
  };

  const showAlert = (type, message) => {
    setAlertMessage({ show: true, type, message });
    setTimeout(() => setAlertMessage({ show: false, type: '', message: '' }), 5000);
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'warning',
      'approved': 'success',
      'rejected': 'error',
      'consolidated': 'info'
    };
    return colors[status] || 'default';
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      {/* Alert Messages */}
      {alertMessage.show && (
        <Alert severity={alertMessage.type} sx={{ mb: 3 }}>
          {alertMessage.message}
        </Alert>
      )}

      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, color: '#1a237e' }}>
          📊 Order Consolidation
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage and optimize weekly order consolidation
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1976d2', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{stats.pending_orders}</Typography>
                  <Typography variant="body2">Pending Orders</Typography>
                </Box>
                <ScheduleIcon sx={{ fontSize: 48, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#388e3c', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">{stats.total_products}</Typography>
                  <Typography variant="body2">Unique Products</Typography>
                </Box>
                <ReportIcon sx={{ fontSize: 48, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#f57c00', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">£{stats.estimated_savings}</Typography>
                  <Typography variant="body2">Est. Savings</Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#7b1fa2', color: 'white' }}>
            <CardContent>
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>Last Consolidation</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {stats.last_consolidation || 'Never'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PlayIcon />}
          onClick={runConsolidation}
          disabled={loading || consolidationData.length === 0}
          size="large"
        >
          Run Consolidation
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ScheduleIcon />}
          onClick={() => setScheduleDialog(true)}
        >
          Configure Schedule
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchConsolidationData}
        >
          Refresh
        </Button>
      </Box>

      {/* Consolidation Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Pending Consolidation Orders
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : consolidationData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography variant="h4" sx={{ mb: 2 }}>📦</Typography>
              <Typography variant="body1" color="text.secondary">
                No pending orders for consolidation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Orders will appear here when staff members place new orders
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>Product</strong></TableCell>
                    <TableCell><strong>Total Quantity</strong></TableCell>
                    <TableCell><strong>Orders Count</strong></TableCell>
                    <TableCell><strong>Staff Members</strong></TableCell>
                    <TableCell><strong>Est. Cost</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {consolidationData.map((order, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {order.product_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Code: {order.product_code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Badge badgeContent={order.total_quantity} color="primary" max={999}>
                          <Chip label={`${order.total_quantity} units`} size="small" />
                        </Badge>
                      </TableCell>
                      <TableCell>{order.order_count}</TableCell>
                      <TableCell>
                        <Tooltip title={order.staff_names?.join(', ') || ''}>
                          <Chip 
                            label={`${order.staff_count} staff`} 
                            size="small" 
                            color="secondary"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          £{order.estimated_cost?.toFixed(2) || '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={order.status} 
                          color={getStatusColor(order.status)} 
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton 
                            color="primary" 
                            size="small"
                            onClick={() => handleViewDetails(order)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Approve">
                          <IconButton 
                            color="success" 
                            size="small"
                            onClick={() => handleApprove(order.product_id)}
                          >
                            <ApproveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton color="warning" size="small">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Schedule Configuration Dialog */}
      <Dialog open={scheduleDialog} onClose={() => setScheduleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Configure Consolidation Schedule
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={schedule.frequency}
                  onChange={(e) => setSchedule({...schedule, frequency: e.target.value})}
                  label="Frequency"
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="biweekly">Bi-weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Day</InputLabel>
                <Select
                  value={schedule.day}
                  onChange={(e) => setSchedule({...schedule, day: e.target.value})}
                  label="Day"
                >
                  <MenuItem value="Monday">Monday</MenuItem>
                  <MenuItem value="Tuesday">Tuesday</MenuItem>
                  <MenuItem value="Wednesday">Wednesday</MenuItem>
                  <MenuItem value="Thursday">Thursday</MenuItem>
                  <MenuItem value="Friday">Friday</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={schedule.time}
                onChange={(e) => setSchedule({...schedule, time: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveSchedule}>Save Schedule</Button>
        </DialogActions>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Consolidated Order Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Product</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedOrder.product_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Total Quantity</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedOrder.total_quantity} units
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Number of Orders</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedOrder.order_count}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Estimated Cost</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    £{selectedOrder.estimated_cost?.toFixed(2) || '0.00'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Staff Members
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedOrder.staff_names?.map((name, idx) => (
                      <Chip key={idx} label={name} size="small" color="primary" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
          <Button 
            variant="outlined" 
            color="error"
            startIcon={<RejectIcon />}
            onClick={() => handleReject(selectedOrder?.product_id, 'Manual rejection')}
          >
            Reject
          </Button>
          <Button 
            variant="contained" 
            color="success"
            startIcon={<ApproveIcon />}
            onClick={() => handleApprove(selectedOrder?.product_id)}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderConsolidation;
